import { t } from "i18next";
import { useMemo } from "react";
import { Path, UseFormReturn } from "react-hook-form";

import { FollowButtonField } from "src/core/components/create-client/FollowButtonField";
import {
  FormUserSearch,
  FormUserSearchMulti,
} from "src/core/components/custom-ui/form-user-search";
import { FormLabel } from "src/core/components/ui/form";
import { ISelectOption } from "src/core/constants/options";
import { Roles } from "src/core/constants/roles";
import { useUser } from "src/core/contexts/UserContext";
import { usePermissions } from "src/core/hooks/usePermissions";

type FollowFolderInfoBase = {
  expertComptable: string;
  isFollowing?: boolean;
  folowers?: string[];
};

const toPinnedOption = (user: {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
}): ISelectOption[] => {
  const label = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  if (!user.id || !label) return [];
  return [{ value: user.id, label }];
};

export const FollowFolderInfo = <T extends FollowFolderInfoBase>({
  form,
}: {
  form: UseFormReturn<T>;
}) => {
  const { user } = useUser();
  const { hasRole } = usePermissions();
  const pinnedExpert = useMemo(() => (user ? toPinnedOption(user) : []), [user]);

  return (
    <div className="grid grid-cols-2 items-start gap-x-30 mb-10">
      <div className="col-span-1">
        <FormUserSearch
          control={form.control}
          name={"expertComptable" as Path<T>}
          label={t("form.createClient.inCharge")}
          pinnedOptions={pinnedExpert}
          clearable
          className="min-w-[310px]"
        />
      </div>
      {hasRole(Roles.COLLABORATEUR) ? (
        <div className="col-span-1 flex flex-col h-full justify-between">
          <FormLabel>{t("form.createClient.followLabel")}</FormLabel>
          <FollowButtonField
            isFollowing={(form.watch("isFollowing" as Path<T>) as boolean | undefined) || false}
            onClick={(event) => {
              event.preventDefault();
              const currentValue =
                (form.watch("isFollowing" as Path<T>) as boolean | undefined) || false;
              (form.setValue as (name: string, value: boolean) => void)(
                "isFollowing",
                !currentValue
              );
            }}
          />
        </div>
      ) : (
        <FormUserSearchMulti
          control={form.control}
          name={"folowers" as Path<T>}
          className="min-w-[310px]"
          label={t("form.createClient.toFollowLabel")}
          placeholder={t("form.createClient.toFollowPlaceholder")}
        />
      )}
    </div>
  );
};
